import { FC, useState } from 'react'
import { useAdPerfActiveTimes } from 'src/reactQuery/useAdPerformance'
import { AdPerfRange } from 'src/services/adPerformanceApi'
import s from './dashboard.module.scss'
import { PillSelect } from './PillSelect'
import { compact, money } from '../format'

// 3-hour buckets, like the design (rows 12am, 3am … 9pm).
const BUCKETS = [0, 3, 6, 9, 12, 15, 18, 21]
const bucketLabel = (h: number) =>
  h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`

interface Props {
  currency: string
  range: AdPerfRange
}

export const ActiveTimes: FC<Props> = ({ currency, range }) => {
  const [metric, setMetric] = useState('clicks')
  // Meta is the only connected platform, so All and Meta show the same data.
  const [platform, setPlatform] = useState('all')
  const { data } = useAdPerfActiveTimes(range, metric)
  const grid = data?.grid ?? Array.from({ length: 7 }, () => Array(24).fill(0))
  // cells[bucket][day]
  const cells = BUCKETS.map((b) =>
    grid.map((day) => day.slice(b, b + 3).reduce((a, v) => a + v, 0))
  )
  const max = Math.max(1, ...cells.flat())
  const fmt = (v: number) =>
    metric === 'spend' ? money(v, currency) : compact(v)

  return (
    <div className={s.card}>
      <h3 className={s.cardTitle}>Most Active Times</h3>
      <div className={s.filters}>
        <PillSelect
          align="left"
          label="Metric"
          onChange={setMetric}
          options={[
            { label: 'Clicks', value: 'clicks' },
            { label: 'Impressions', value: 'impressions' },
            { label: 'Link clicks', value: 'link_clicks' },
            { label: 'Spend', value: 'spend' },
          ]}
          value={metric}
        />
        <PillSelect
          align="left"
          label="Platform"
          onChange={setPlatform}
          options={[
            { label: 'All', value: 'all' },
            { label: 'Meta', value: 'meta' },
          ]}
          value={platform}
        />
      </div>
      <div className={s.heat}>
        <span />
        {(data?.days ?? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']).map(
          (d) => (
            <span className={s.heatDay} key={d}>
              {d}
            </span>
          )
        )}
        {cells.map((row, i) => [
          <span className={s.heatHour} key={`l${BUCKETS[i]}`}>
            {bucketLabel(BUCKETS[i])}
          </span>,
          ...row.map((v, d) => (
            <div
              className={s.heatCell}
              key={`${BUCKETS[i]}-${d}`}
              style={{
                background: `rgba(64, 128, 255, ${0.12 + (v / max) * 0.88})`,
              }}
            >
              <span className={s.heatTip}>
                <small>
                  {data?.days?.[d] ?? ''} {bucketLabel(BUCKETS[i])}–
                  {bucketLabel((BUCKETS[i] + 3) % 24)}
                </small>
                <strong>{fmt(v)}</strong>
              </span>
            </div>
          )),
        ])}
      </div>
      <p className={s.syncNote}>
        Hours in the ad account&apos;s time zone ({data?.timezone ?? '…'}).
      </p>
    </div>
  )
}
